import type { SalonStockerHttpTypes } from "@repo/api-sdk";

type AdHocItemCapture = SalonStockerHttpTypes.AdHocItemCapture;

export type AdHocCaptureParseResult =
  | { ok: true; capture: AdHocItemCapture }
  | { ok: false; error: string };

/**
 * Parses and validates the clipboard JSON the browser extension writes when
 * Caroline (the concierge operator) captures a product from a supplier's
 * Shopify storefront (KAN-193) - the paste-in form's textarea reads this back
 * to prefill the add form.
 *
 * A pure function, deliberately separate from the dialog component, so the
 * parse/validate step is unit-testable without a DOM. Rejects rather than
 * guesses at anything that isn't recognisably an `AdHocItemCapture` at
 * `schema_version: 1` - a stale/newer extension build, or a paste of
 * something else entirely, must surface an inline error, never a
 * half-populated form or a crash. Every rejection returns a specific,
 * human-readable reason rather than a generic "invalid" message, since the
 * only recovery available to Caroline is re-copying from the supplier's page.
 */
export function parseAdHocCapture(raw: string): AdHocCaptureParseResult {
  const trimmed = raw.trim();
  if (!trimmed) {
    return {
      ok: false,
      error: "Paste the product you copied from the supplier's site."
    };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return {
      ok: false,
      error:
        "That doesn't look like valid JSON. Copy the product again and paste it here."
    };
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return { ok: false, error: "That doesn't look like a captured product." };
  }

  const value = parsed as Record<string, unknown>;

  if (value.schema_version !== 1) {
    return {
      ok: false,
      error:
        "This capture is from a different extension version. Update the extension and try again."
    };
  }

  if (
    typeof value.shopify_variant_id !== "string" ||
    !value.shopify_variant_id
  ) {
    return {
      ok: false,
      error:
        "This capture is missing the product variant. Try copying it again."
    };
  }

  if (
    typeof value.shopify_product_id !== "string" ||
    !value.shopify_product_id
  ) {
    return {
      ok: false,
      error: "This capture is missing the product. Try copying it again."
    };
  }

  if (typeof value.title !== "string" || !value.title) {
    return {
      ok: false,
      error: "This capture is missing the product title. Try copying it again."
    };
  }

  const sourceHosts = value.source_hosts as Record<string, unknown> | undefined;
  if (
    typeof sourceHosts !== "object" ||
    sourceHosts === null ||
    typeof sourceHosts.browsing_host !== "string" ||
    !sourceHosts.browsing_host
  ) {
    return {
      ok: false,
      error:
        "This capture is missing the supplier's site. Try copying it again."
    };
  }

  return {
    ok: true,
    capture: {
      schema_version: 1,
      shopify_variant_id: value.shopify_variant_id,
      shopify_product_id: value.shopify_product_id,
      title: value.title,
      image_url:
        typeof value.image_url === "string" ? value.image_url : undefined,
      price:
        typeof value.price === "number" && Number.isFinite(value.price)
          ? value.price
          : undefined,
      variant_title:
        typeof value.variant_title === "string"
          ? value.variant_title
          : undefined,
      source_hosts: {
        browsing_host: sourceHosts.browsing_host,
        canonical_myshopify_host:
          typeof sourceHosts.canonical_myshopify_host === "string"
            ? sourceHosts.canonical_myshopify_host
            : undefined
      }
    }
  };
}

/**
 * Parses the editable price field's raw text into a validated, positive
 * major-units number, or `null` when it isn't one. Deliberately major units
 * throughout - `StoreAddAdHocLineItem.unit_price` is documented as "already
 * in the cart region's currency" (major units), so there is no `*100`
 * conversion to hand-roll here; `utils/money.ts` is reserved for formatting
 * the value for display, not for this field's own parsing.
 */
export function parseAdHocPriceInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value;
}

/**
 * Parses the quantity field's raw text into a validated positive integer, or
 * `null` when it isn't one. Rejects decimals, negatives, zero and anything
 * non-numeric outright rather than clamping - the field is small enough that
 * a silent clamp would just as easily hide a typo as fix one.
 */
export function parseAdHocQuantityInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed || !/^\d+$/.test(trimmed)) return null;

  const value = Number(trimmed);
  if (!Number.isInteger(value) || value <= 0) return null;
  return value;
}

export interface AdHocSubmitCandidate {
  /** The Select's current value - `null` while nothing is chosen. */
  sellerId: string | null;
  /** The parsed, validated price, or `null` while the field is empty/invalid. */
  unitPrice: number | null;
  /** The parsed, validated quantity, or `null` while the field is empty/invalid. */
  quantity: number | null;
}

/**
 * Whether the paste-in form has everything it needs to submit.
 *
 * A pure predicate over the form's already-parsed state, kept separate from
 * whether a capture has been parsed at all (callers gate on that themselves -
 * there is nothing to submit without one). Requires an explicit `sellerId`:
 * `suggested_seller_id` only pre-selects the dropdown, it never substitutes
 * for Caroline's own choice (see `StoreAddAdHocLineItem.seller_id`'s doc), so
 * this never treats "a suggestion exists" as "a seller is chosen".
 */
export function canSubmitAdHocForm(candidate: AdHocSubmitCandidate): boolean {
  return (
    Boolean(candidate.sellerId) &&
    candidate.unitPrice !== null &&
    candidate.quantity !== null
  );
}
